import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import { Box, Button, CircularProgress, MenuItem, TextField, Typography } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';

import { deleteEventDocumentByReference, getEventDocumentByReference, uploadEventDocument } from 'core/API/events';
import { eventsService, getEventDocumentTemplatePdfUrl } from 'core/services/eventsService';
import { EventDocumentPdfEditorModal } from 'module/settings/settingsGameMasterTabs/EventsSetting/EventDocumentPdfEditorModal';

import AddEventModalStyle from './AddEventModal.module.scss';

interface BanquetEventOrderTemplateFillProps {
	setFieldValue: (field: string, value: string) => void;
	value: string;
	workplaceId: number;
}

interface PreviewItem {
	ref: string;
	url: string;
	label: string;
}

/**
 * Dropdown of active banquet PDF templates (GameMaster → Events). User fills in WebViewer and the filled PDF is
 * uploaded via event-documents/upload. No direct file upload UI—templates only.
 */
export default function BanquetEventOrderTemplateFill({
	setFieldValue,
	value,
	workplaceId,
}: BanquetEventOrderTemplateFillProps): JSX.Element {
	const [selectedId, setSelectedId] = useState<string>('');
	const [editorOpen, setEditorOpen] = useState(false);
	const [pdfUrl, setPdfUrl] = useState<string | null>(null);
	const [editorTitle, setEditorTitle] = useState('');
	const [previewLoading, setPreviewLoading] = useState(false);
	const [previewItems, setPreviewItems] = useState<PreviewItem[]>([]);

	const { data: templates = [], isLoading } = useQuery(
		['event-document-templates', workplaceId],
		() => eventsService.getEventDocumentTemplates({ workplace: workplaceId }),
		{
			staleTime: 60_000,
			enabled: Number.isFinite(workplaceId) && workplaceId > 0,
		}
	);

	const activeTemplates = useMemo(
		() => templates.filter((t) => t.active !== false).sort((a, b) => a.sortOrder - b.sortOrder),
		[templates]
	);

	const selectedTemplate = useMemo(
		() => activeTemplates.find((t) => String(t.id) === selectedId),
		[activeTemplates, selectedId]
	);

	const { mutateAsync: uploadFile, isLoading: isUploading } = useMutation(uploadEventDocument);
	const { mutateAsync: deleteUploadedFile } = useMutation(deleteEventDocumentByReference);

	const openEditor = useCallback(() => {
		if (!selectedTemplate?.filePath) return;
		const url = getEventDocumentTemplatePdfUrl(selectedTemplate.filePath);
		if (!url) return;
		setEditorTitle(selectedTemplate.name);
		setPdfUrl(url);
		setEditorOpen(true);
	}, [selectedTemplate]);

	const closeEditor = useCallback(() => {
		setEditorOpen(false);
		setPdfUrl(null);
	}, []);

	const handleSaveFilledPdf = async (file: File) => {
		if (!selectedTemplate?.id || !workplaceId) return;
		const data = await uploadFile({
			file,
			template: `/api/event_document_templates/${selectedTemplate.id}`,
			workplace: `/api/workplaces/${workplaceId}`,
		});
		const docValue = data?.['@id'] || data?.path || data?.filePath || data?.file_path || '';
		if (docValue) {
			const existing = (value || '')
				.split(',')
				.map((v) => v.trim())
				.filter(Boolean);
			if (!existing.includes(docValue)) {
				setFieldValue('banquet_event_order', [...existing, docValue].join(','));
			}
		}
	};

	useEffect(() => {
		let cancelled = false;
		const uploadsBase = (process.env.REACT_APP_IMAGE_URL || '').replace(/\/$/, '');

		const refs = (value || '')
			.split(',')
			.map((v) => v.trim())
			.filter(Boolean);

		const resolveOne = async (ref: string): Promise<PreviewItem> => {
			if (/^https?:\/\//i.test(ref)) {
				return { ref, url: ref, label: ref.split('/').pop() || 'Open PDF' };
			}

			if (ref.startsWith('/api/')) {
				try {
					const doc = await getEventDocumentByReference(ref);
					const raw = doc.filePath || doc.file_path || doc.path || '';
					if (!raw) return { ref, url: '', label: 'Preview unavailable' };
					const clean = String(raw).replace(/^\/+/, '').replace(/^uploads\//, '');
					const url = `${uploadsBase}/${clean.startsWith('event_documents/') ? clean : `event_documents/${clean}`}`;
					const label = doc.fileName || doc.file_name || clean.split('/').pop() || 'Open PDF';
					return { ref, url, label };
				} catch {
					return { ref, url: '', label: 'Preview unavailable' };
				}
			}

			const clean = ref.replace(/^\/+/, '').replace(/^uploads\//, '');
			const url = `${uploadsBase}/${clean.startsWith('event_documents/') ? clean : `event_documents/${clean}`}`;
			return { ref, url, label: clean.split('/').pop() || 'Open PDF' };
		};

		const resolveAll = async () => {
			if (!refs.length) {
				if (!cancelled) {
					setPreviewItems([]);
				}
				return;
			}
			setPreviewLoading(true);
			const items = await Promise.all(refs.map((ref) => resolveOne(ref)));
			if (!cancelled) {
				setPreviewItems(items);
				setPreviewLoading(false);
			}
		};

		resolveAll();
		return () => {
			cancelled = true;
		};
	}, [value]);

	const handleDeleteRef = async (ref: string) => {
		if (ref.startsWith('/api/event_documents/') || ref.startsWith('/api/event-documents/')) {
			try {
				await deleteUploadedFile(ref);
			} catch (e) {
				console.error('Failed to delete uploaded event document', e);
				return;
			}
		}
		const next = (value || '')
			.split(',')
			.map((v) => v.trim())
			.filter(Boolean)
			.filter((v) => v !== ref);
		setFieldValue('banquet_event_order', next.join(','));
	};

	return (
		<div className={AddEventModalStyle.banquetTemplateSection}>
			{isLoading ? (
				<CircularProgress size={20} />
			) : activeTemplates.length === 0 ? (
				<p className={AddEventModalStyle.banquetTemplateHint}>
					No Banquet PDF templates found. Add them under Setting → Events → Banquet event order.
				</p>
			) : (
				<>
					<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
						<TextField
							id="banquet-template-select"
							select
							fullWidth
							size="small"
							value={selectedId}
							onChange={(e) => setSelectedId(e.target.value)}
							SelectProps={{
								displayEmpty: true,
								IconComponent: KeyboardArrowDownRoundedIcon,
							}}
							sx={{
								'& .MuiOutlinedInput-root': {
									borderRadius: '6px',
									backgroundColor: '#fff',
									fontFamily: 'Gotham',
									color: '#3c5471',
								},
								'& .MuiSelect-select': {
									color: '#3c5471',
									fontFamily: 'Gotham',
									paddingRight: '36px',
								},
								'& .MuiSelect-icon': {
									color: '#3c5471',
									right: 10,
									fontSize: 22,
								},
							}}
						>
							<MenuItem value="">
								<span style={{ color: '#8a96a8' }}>Select a PDF template...</span>
							</MenuItem>
							{activeTemplates.map((t) => (
								<MenuItem key={t.id} value={String(t.id)}>
									{t.name}
								</MenuItem>
							))}
						</TextField>
						<Button
							type="button"
							variant="contained"
							onClick={openEditor}
							disabled={!selectedTemplate || isUploading}
							sx={{
								backgroundColor: 'rgb(136, 155, 181)',
								color: '#fff',
								textTransform: 'none',
								fontFamily: 'Gotham',
								fontWeight: 500,
								borderRadius: '4px',
								'&:hover': { backgroundColor: 'rgb(110, 132, 162)' },
								'&.Mui-disabled': {
									backgroundColor: 'rgb(198, 207, 220)',
									color: '#fff',
								},
							}}
						>
							{isUploading ? 'Uploading…' : 'Fill PDF & attach'}
						</Button>
					</Box>
				</>
			)}

			{value
				? previewLoading
					? (
						<div className={AddEventModalStyle.uploadedFileNameRow}>
							<span className={AddEventModalStyle.uploadedFileNameText}>Loading preview...</span>
						</div>
					)
					: previewItems.map((item) => (
						<div className={AddEventModalStyle.uploadedFileNameRow} key={item.ref}>
							{item.url ? (
								<a
									href={item.url}
									target="_blank"
									rel="noopener noreferrer"
									className={AddEventModalStyle.uploadedFileNameLink}
								>
									{item.label}
								</a>
							) : (
								<span className={AddEventModalStyle.uploadedFileNameText}>{item.label}</span>
							)}
							<button
								type="button"
								className={AddEventModalStyle.removePdfButton}
								onClick={() => {
									void handleDeleteRef(item.ref);
								}}
								aria-label="Remove attached PDF"
							>
								<DeleteIcon fontSize="small" />
							</button>
						</div>
					))
				: null}

			<EventDocumentPdfEditorModal
				open={editorOpen}
				onClose={closeEditor}
				pdfUrl={pdfUrl}
				documentTitle={editorTitle}
				onSave={handleSaveFilledPdf}
			/>
		</div>
	);
}
